<template>
  <b-card
    v-if="data"
    md="6"
    sm="6"
    no-body
    class="card-statistics"
  >
    <b-card-header
      class="d-flex flex-wrap justify-content-between align-items-center"
      style="gap: 10px;"
    >
      <b-card-title class="chart-title">
        {{ $t('Ticket Statistics') }}
        <tooltip-help text="Displays a summary of support ticket statistics, including open, in-progress, pending, and closed tickets." />
      </b-card-title>
      <b-card-text class="font-small-2 mr-25 mb-0" />
      <b-button
        v-if="showRefreshButton"
        v-ripple.400="'rgba(113, 102, 240, 0.15)'"
        size="sm"
        variant="outline-primary"
        class="widgetrefresh-button d-flex align-items-center justify-content-center shadow-custom"
        :style="cardBorder"
        :disabled="!canRefresh"
        @click.stop="refreshWidget"
      >
        <feather-icon
          icon="RefreshCwIcon"
          class="mr-50 refresh-icon"
        />
        <span class="align-middle refresh-span" />
        <time-ago
          class="timeAgo"
          :datetime="lastRefreshTime ? new Date(parseInt(lastRefreshTime, 10)).toISOString() : lastUpdatedAt"
          refresh
          locale="en"
          :long="longString"
          :style="{color: '#1C1F32'}"
        />
      </b-button>
    </b-card-header>
    <b-card-body class="statistics-body">
      <div class="d-flex flex-wrap justify-content-between">
        <div
          v-for="item in data"
          :key="item.icon"
          class="flex-grow-1 mx-50 mb-1"
          style="min-width: 200px;"
        >
          <b-media
            no-body
            class="stat-card"
            :style="{ backgroundColor: item.bgcolor}"
          >
            <b-media-body class="my-auto">
              <h4
                v-b-tooltip.hover.v-primary
                class="font-weight-bolder mb-1 stat-number"
                :title="item.subtitle === 'Orders Count' ? item.title.toLocaleString() : __numberWithCommas(item.title, true)"
              >
                {{ __numberWithCommas(smartFormatter(item.title)) }}
              </h4>
              <b-card-text class="font-small-3 mb-0 font-weight-bolder stat-subtitle">
                {{ $t(item.subtitle) }}
              </b-card-text>
            </b-media-body>
            <b-media-aside
              class="avatar-container"
            >
              <b-avatar
                size="48"
                :style="{ backgroundColor: item.avatarBg || shadeColor(item.bgcolor, -10), color: item.iconColor }"
              >
                <feather-icon
                  size="24"
                  :icon="item.icon"
                />
              </b-avatar>
            </b-media-aside>
          </b-media>
        </div>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardTitle,
  BCardText,
  BCardBody,
  BMedia,
  BMediaAside,
  BAvatar,
  BMediaBody,
  VBTooltip,
  BButton,
} from 'bootstrap-vue'
import { TimeAgo } from 'vue2-timeago'
import RefreshMixin from '@/@core/mixins/ui/refresh'
import TooltipHelp from '../Dashboard/TooltipHelp.vue'

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BCard,
    BCardHeader,
    BCardTitle,
    BCardText,
    BCardBody,
    BMedia,
    BAvatar,
    BMediaAside,
    BMediaBody,
    TimeAgo,
    BButton,
    TooltipHelp,
  },
  mixins: [RefreshMixin],
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    lastUpdatedAt: {
      type: String,
      default: null,
    },
    queue: {
      type: String,
      required: true,
    },
    showRefreshButton: {
      type: Boolean,
      default: true, // or false, depending on your default preference
    },
  },
  data() {
    return {
      refreshTimestamp: new Date(),
      longString: true,
      lastRefreshTime: (JSON.parse(localStorage.getItem('ticketDashboardLastUpdatedAt')) || {}).Statistics || 0,
      now: Date.now(),
      timer: null,
    }
  },
  computed: {
    cardBorder() {
      return {
        border: '1px solid var(--primary) !important',
        opacity: this.canRefresh ? 1 : 0.5,
      }
    },
    canRefresh() {
      const diff = this.now - parseInt(this.lastRefreshTime, 10)
      return diff >= 60000
    },
  },
  created() {
    this.timer = setInterval(() => {
      this.now = Date.now()
    }, 1000)
    const timestamps = JSON.parse(localStorage.getItem('ticketDashboardLastUpdatedAt')) || {}
    if (!timestamps.Statistics) {
      this.refreshWidget()
    }
  },
  beforeDestroy() {
    clearInterval(this.timer)
  },
  methods: {
    refreshWidget() {
      if (!this.canRefresh) return
      const timestamp = Date.now()
      this.lastRefreshTime = timestamp
      const timestampsObj = JSON.parse(localStorage.getItem('ticketDashboardLastUpdatedAt')) || {}
      timestampsObj.Statistics = timestamp
      localStorage.setItem('ticketDashboardLastUpdatedAt', JSON.stringify(timestampsObj))
      this.$emit('refresh', this.queue)
      this.refreshTimestamp = new Date()
    },
    numberWithCommas(x, isFloat = false) {
      if (Number.isNaN(x)) return x
      const parts = parseFloat(x).toFixed(isFloat ? 2 : 0).split('.')
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      return parts.join('.')
    },
    smartFormatter(value) {
      const abs = Math.abs(value)
      if (abs >= 1.0e9) {
        return `${(value / 1.0e9).toFixed(1)}B`
      }
      if (abs >= 1.0e6) {
        return `${(value / 1.0e6).toFixed(1)}M`
      }
      if (abs >= 1.0e3) {
        return `${(value / 1.0e3).toFixed(1)}K`
      }
      return value.toString()
    },
    shadeColor(color, percent) {
      if (!color) return color
      let R = parseInt(color.substring(1, 3), 16)
      let G = parseInt(color.substring(3, 5), 16)
      let B = parseInt(color.substring(5, 7), 16)

      R = parseInt((R * (100 + percent)) / 100, 10)
      G = parseInt((G * (100 + percent)) / 100, 10)
      B = parseInt((B * (100 + percent)) / 100, 10)

      R = (R < 255) ? R : 255
      G = (G < 255) ? G : 255
      B = (B < 255) ? B : 255

      const RR = ((R.toString(16).length === 1) ? `0${R.toString(16)}` : R.toString(16))
      const GG = ((G.toString(16).length === 1) ? `0${G.toString(16)}` : G.toString(16))
      const BB = ((B.toString(16).length === 1) ? `0${B.toString(16)}` : B.toString(16))

      return `#${RR}${GG}${BB}`
    },
  },
}
</script>
<style lang="scss" scoped>
@import '../@core/scss/vue/pages/widget-refresh.scss';
@media (max-width: 745px) {
  .card-statistics .col-12.col-md-6 {
    margin-top: 1rem;
    width: 100% !important;
  }
}
@media (min-width: 768px) and (max-width: 1024px) {
  .card-statistics .col-12.col-md-6 {
    flex: 0 0 100%;
    max-width: 100%;
    margin-bottom: 1rem;
  }
}
.stat-card {
  width: 100% !important;
  height: 110px;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  margin-left: 0;
  flex: 1 0 auto; /* Allow cards to grow */
  position: relative;
}
.avatar-container {
  position: absolute; /* Position avatar absolutely */
  top: 16px;
  right: 16px;
}
.stat-number {
  font-size: 28px; /* Increased font size */
  font-weight: 700;
}
.stat-subtitle {
  font-size: 16px !important; /* Increased font size */
  color: #5e5873; /* Optional: Change color for better visibility */
  font-weight: 600;
}
.refresh-icon {
  color: #1C1F32;
}
</style>
